import { createContext } from 'react';
import { AskCardLink, AskPaneContent } from './extractCardLinks';

/** The answer a card was opened from. */
export interface AskAnswerRef {
  id: string;
  /** Its markdown, which is where the rest of its cards are read from. */
  content: string;
}

export interface AskCardsContextValue {
  /**
   * Puts an answer's cards — the ones it names, or the list it links to — in the pane beside the
   * conversation, or takes them out if that answer's are already there. Absent where there is no
   * room for a pane, a screen too narrow to split, so an answer offers it only where it can happen.
   */
  togglePane?: (content: AskPaneContent, answerId: string) => void;
  /**
   * Whether the user has this workspace, so a link into one they cannot open offers nothing. Asked
   * of the page rather than of each answer, which would each build the same set of the user's
   * workspaces for themselves.
   */
  isWorkspaceAvailable: (channelSlug: string) => boolean;
  /**
   * The answer whose cards the pane is showing, so that answer can be marked as the one open
   * beside the conversation — several may have cards, and the pane alone cannot say whose.
   */
  paneAnswerId?: string;
  /**
   * Opens one of an answer's cards without leaving Ask. The answer's cards go in the pane as a
   * click on the answer would put them, and next and previous step through them.
   */
  openCard: (card: AskCardLink, answer: AskAnswerRef) => void;
}

/**
 * What the Ask page can do with the cards an answer names. Provided by the page alone: the
 * floating panel sits over some other screen, and a card opened from it opens in its workspace
 * as any card link does (see `useOpenAskCard`).
 */
export const AskCardsContext = createContext<AskCardsContextValue | undefined>(undefined);
